<script setup lang="ts">
import type { Project } from '~/utilities/pyscript-api-models';
import ProjectCatalogListItem from '~/components/project-catalog/ProjectCatalogListItem.vue';
import ProjectCatalogListColumns from '~/components/project-catalog/ProjectCatalogListColumns.vue';
import { useSortPreference } from '~/composables/sort-preference';
import IconChevronDown from '~icons/carbon/chevron-down';

defineProps<{
  projectList: Project[];
  animateLeave?: boolean;
  tagClickHandler: (...args: any) => void;
}>();

defineEmits<{
  (e: 'projectDeleted', value: Project): void;
  (e: 'projectUpdated', value: Project): void;
}>();

const { updateSort, savedSort } = useSortPreference();
</script>

<template>
  <section
    class="rounded border border-new-gray-100/70 bg-white dark:border-new-gray-950/60 dark:bg-gray-675"
  >
    <div
      class="hidden border-b border-new-gray-50 py-0.5 text-sm font-medium dark:border-new-gray-950/60 sm:block"
    >
      <ProjectCatalogListColumns>
        <template #title>
          <button
            class="flex w-full items-center gap-2 hover:underline"
            @click="
              updateSort({
                sortBy: 'name',
                sortOrder: savedSort.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            "
          >
            <span>Title</span>
            <IconChevronDown
              class="shrink-0 text-13"
              :class="[
                savedSort.sortBy === 'name' && savedSort.sortOrder === 'asc' ? 'rotate-180' : '',
                savedSort.sortBy === 'name' ? '' : 'opacity-0',
              ]"
            />
          </button>
        </template>

        <template #description>
          <span class="cursor-default">Description</span>
        </template>

        <template #tag>
          <span class="cursor-default">Tag</span>
        </template>

        <template #modified>
          <button
            class="flex w-full items-center justify-end gap-2 hover:underline"
            @click="
              updateSort({
                sortBy: 'updated_at',
                sortOrder: savedSort.sortOrder === 'desc' ? 'asc' : 'desc',
              })
            "
          >
            <span class="whitespace-nowrap">Last Modified</span>
            <IconChevronDown
              class="-mr-5 shrink-0 text-13"
              :class="[
                savedSort.sortBy === 'updated_at' && savedSort.sortOrder === 'asc'
                  ? 'rotate-180'
                  : '',
                savedSort.sortBy === 'updated_at' ? '' : 'opacity-0',
              ]"
            />
          </button>
        </template>
      </ProjectCatalogListColumns>
    </div>

    <TransitionGroup
      tag="section"
      :css="Boolean(animateLeave)"
      name="project-list-slide"
      class="relative"
    >
      <ProjectCatalogListItem
        v-for="(proj, index) of projectList"
        :key="proj.id"
        :project="proj"
        :tag-click-handler="tagClickHandler"
        :last-item="index === projectList.length - 1"
        @project-deleted="$emit('projectDeleted', $event)"
        @project-updated="$emit('projectUpdated', $event)"
      />
    </TransitionGroup>
  </section>
</template>

<style lang="postcss" scoped>
/* Only add animations if the user doesn't prefer reduced motion. */
@media (prefers-reduced-motion: no-preference) {
  .project-list-slide {
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: scaleY(0.01) translate(30px, 0);
    }

    &-leave-active {
      position: absolute;
    }
  }
}
</style>
